import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import type {
  CategoryDto,
  ScorecardCheckDto,
  SeverityDto,
  SnapshotDto,
  TeamScorecardSummaryDto,
} from '@internal/scorecards-common';

export type FactSchema = {
  id: string;
  schema: Record<string, { type: string; description?: string }>;
};

export interface ScorecardsApi {
  getChecks(): Promise<ScorecardCheckDto[]>;
  /** projectPath is the full GitLab path_with_namespace, e.g. "my-group/my-repo". */
  getEntitySnapshots(projectPath: string): Promise<SnapshotDto[]>;
  getCategories(): Promise<CategoryDto[]>;
  getSeverities(): Promise<SeverityDto[]>;
  /** team is the GitLab team slug, e.g. "team-bhabha". */
  getTeamSummary(team: string): Promise<TeamScorecardSummaryDto>;
}

export const scorecardsApiRef = createApiRef<ScorecardsApi>({
  id: 'plugin.zf-scorecards.service',
});

export class ScorecardsApiClient implements ScorecardsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getChecks(): Promise<ScorecardCheckDto[]> {
    const base = await this.discoveryApi.getBaseUrl('zf-scorecards');
    const all: ScorecardCheckDto[] = [];
    const pageSize = 100;
    let page = 1;
    let total: number;
    do {
      const url = new URL(`${base}/checks`);
      url.searchParams.set('page', String(page));
      url.searchParams.set('pageSize', String(pageSize));
      const res = await this.fetchApi.fetch(url.toString());
      if (!res.ok) throw new Error(`Failed to fetch checks: ${res.status}`);
      const body = await res.json();
      total = body.total as number;
      if ((body.items as unknown[]).length === 0) break;
      all.push(...body.items);
      page++;
    } while (all.length < total);
    return all;
  }

  async getEntitySnapshots(projectPath: string): Promise<SnapshotDto[]> {
    const base = await this.discoveryApi.getBaseUrl('zf-scorecards');
    const url = new URL(
      `${base}/entities/${encodeURIComponent(projectPath)}/results`,
    );
    url.searchParams.set('pageSize', '100');
    const res = await this.fetchApi.fetch(url.toString());
    if (!res.ok) throw new Error(`Failed to fetch snapshots: ${res.status}`);
    const body = await res.json();
    return body.items;
  }

  async getSeverities(): Promise<SeverityDto[]> {
    const base = await this.discoveryApi.getBaseUrl('zf-scorecards');
    const res = await this.fetchApi.fetch(`${base}/severities`);
    if (!res.ok) throw new Error(`Failed to fetch severities: ${res.status}`);
    return res.json();
  }

  async getCategories(): Promise<CategoryDto[]> {
    const base = await this.discoveryApi.getBaseUrl('zf-scorecards');
    const res = await this.fetchApi.fetch(`${base}/categories`);
    if (!res.ok) throw new Error(`Failed to fetch categories: ${res.status}`);
    return res.json();
  }

  async getTeamSummary(team: string): Promise<TeamScorecardSummaryDto> {
    const base = await this.discoveryApi.getBaseUrl('zf-scorecards');
    const res = await this.fetchApi.fetch(
      `${base}/teams/${encodeURIComponent(team)}/summary`,
    );
    if (!res.ok) throw new Error(`Failed to fetch team summary: ${res.status}`);
    return res.json();
  }
}
